import type {
  AdminUpdateWorkspace,
  CreateWorkspace,
  Workspace
} from '@stratumn/studio-shared';
import { action, makeObservable, observable, runInAction } from 'mobx';

import { newError } from '@/services/errors/errors';
import { HTTPWrapper } from '@/utils/http';

export class WorkspaceStore {
  workspaces: Map<string, Workspace[]> = new Map();
  currentWorkspace: Workspace | null = null;
  loadingProcesses: Set<string> = new Set();
  loadingWorkspace = false;

  private httpWrapper: HTTPWrapper<unknown>;
  private fetchControllers: Map<string, AbortController> = new Map();

  constructor() {
    this.httpWrapper = new HTTPWrapper<unknown>('process');

    makeObservable(this, {
      workspaces: observable,
      currentWorkspace: observable,
      loadingProcesses: observable,
      loadingWorkspace: observable,
      fetchWorkspaces: action.bound,
      fetchWorkspace: action.bound,
      createWorkspace: action.bound,
      updateWorkspace: action.bound,
      deleteWorkspace: action.bound,
      adminUpdatePublishedWorkflowIds: action.bound
    });
  }

  isLoading(processId: string): boolean {
    return this.loadingProcesses.has(processId);
  }

  getWorkspacesForProcess(processId: string): Workspace[] {
    const workspaces = this.workspaces.get(processId) ?? [];
    return [...workspaces].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }

  async fetchWorkspaces(processId: string): Promise<Workspace[]> {
    // Abort any in-flight fetch for this processId to prevent stale results
    const existingController = this.fetchControllers.get(processId);
    if (existingController) {
      existingController.abort();
    }

    const controller = new AbortController();
    this.fetchControllers.set(processId, controller);

    runInAction(() => {
      this.loadingProcesses.add(processId);
    });

    try {
      const response = await this.httpWrapper.get<Workspace[]>(
        `/${processId}/workspaces`
      );

      // Check if this fetch was aborted while awaiting
      if (controller.signal.aborted) {
        return this.workspaces.get(processId) ?? [];
      }

      const workspaces = response ?? [];

      runInAction(() => {
        this.workspaces.set(processId, workspaces);
        this.loadingProcesses.delete(processId);
      });

      return workspaces;
    } catch (error) {
      if (controller.signal.aborted) {
        return this.workspaces.get(processId) ?? [];
      }
      runInAction(() => {
        this.loadingProcesses.delete(processId);
      });
      newError('WKSP-fetch', error);
      throw error;
    } finally {
      // Clean up controller reference if it's still ours
      if (this.fetchControllers.get(processId) === controller) {
        this.fetchControllers.delete(processId);
      }
    }
  }

  async createWorkspace(
    processId: string,
    dto: CreateWorkspace
  ): Promise<Workspace> {
    const response = await this.httpWrapper.post<Workspace>(
      `/${processId}/workspaces`,
      dto
    );

    if (!response) {
      throw new Error('Failed to create workspace');
    }

    runInAction(() => {
      const current = this.workspaces.get(processId) ?? [];
      this.workspaces.set(processId, [...current, response]);
    });

    return response;
  }

  async updateWorkspace(
    processId: string,
    workspaceId: string,
    dto: Partial<CreateWorkspace>
  ): Promise<void> {
    const response = await this.httpWrapper.patch<Workspace>(
      `/${processId}/workspaces/${workspaceId}`,
      dto
    );

    if (!response) {
      return;
    }

    runInAction(() => {
      const current = this.workspaces.get(processId) ?? [];
      const index = current.findIndex(w => w.id === workspaceId);
      if (index >= 0) {
        current[index] = response;
        this.workspaces.set(processId, [...current]);
      }
    });
  }

  async deleteWorkspace(processId: string, workspaceId: string): Promise<void> {
    await this.httpWrapper.delete(`/${processId}/workspaces/${workspaceId}`);

    runInAction(() => {
      const current = this.workspaces.get(processId) ?? [];
      this.workspaces.set(
        processId,
        current.filter(w => w.id !== workspaceId)
      );
      if (this.currentWorkspace?.id === workspaceId) {
        this.currentWorkspace = null;
      }
    });
  }

  async fetchWorkspace(
    processId: string,
    workspaceId: string
  ): Promise<Workspace | null> {
    runInAction(() => {
      this.loadingWorkspace = true;
    });

    try {
      const response = await this.httpWrapper.get<Workspace>(
        `/${processId}/workspaces/${workspaceId}`
      );

      runInAction(() => {
        this.currentWorkspace = response ?? null;
        this.loadingWorkspace = false;
      });

      return response ?? null;
    } catch (error) {
      runInAction(() => {
        this.loadingWorkspace = false;
      });
      newError('WKSP-fetchOne', error);
      throw error;
    }
  }

  async adminUpdatePublishedWorkflowIds(
    processId: string,
    workspaceId: string,
    dto: AdminUpdateWorkspace
  ): Promise<void> {
    try {
      const response = await this.httpWrapper.patch<Workspace>(
        `/${processId}/workspaces/${workspaceId}/admin`,
        dto
      );

      if (!response) {
        return;
      }

      runInAction(() => {
        this.currentWorkspace = response;
        // Also update in the list cache
        const current = this.workspaces.get(processId) ?? [];
        const index = current.findIndex(w => w.id === workspaceId);
        if (index >= 0) {
          current[index] = response;
          this.workspaces.set(processId, [...current]);
        }
      });
    } catch (error) {
      newError('WKSP-adminUpdate', error);
      throw error;
    }
  }

  async preAllocateWorkflowIds(
    processId: string,
    workspaceId: string
  ): Promise<void> {
    try {
      await this.httpWrapper.post<{ success: boolean }>(
        `/${processId}/workspaces/${workspaceId}/pre-allocate`,
        {}
      );
      // Refresh workspace to get new published_workflow_ids
      await this.fetchWorkspace(processId, workspaceId);
    } catch (error) {
      newError('WKSP-preAllocate', error);
      throw error;
    }
  }
}
